import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Switch,
  FormControlLabel,
  Divider
} from '@mui/material';
import { EditNote } from '@mui/icons-material';

interface SignatureDialogProps {
  open: boolean;
  onClose: () => void;
  onSaveSignature: (signature: string, enabled: boolean) => void;
  currentSignature?: string;
  signatureEnabled?: boolean;
}

const SignatureDialog: React.FC<SignatureDialogProps> = ({
  open,
  onClose,
  onSaveSignature,
  currentSignature = '',
  signatureEnabled = false
}) => {
  const [signature, setSignature] = useState(currentSignature);
  const [enabled, setEnabled] = useState(signatureEnabled);

  useEffect(() => {
    if (open) {
      setSignature(currentSignature);
      setEnabled(signatureEnabled);
    }
  }, [open, currentSignature, signatureEnabled]);

  const handleSave = () => {
    onSaveSignature(signature, enabled);
    onClose();
  };

  const handleCancel = () => {
    setSignature(currentSignature);
    setEnabled(signatureEnabled);
    onClose();
  };

  // Common signature templates
  const templates = [
    {
      name: 'Professional',
      content: `Best regards,
[Agent Name]
[Company Name]
[Email] | [Phone]`
    },
    {
      name: 'Casual',
      content: `Thanks,
[Agent Name]
Support Team`
    },
    {
      name: 'Detailed',
      content: `Best regards,

[Agent Name]
Customer Support Specialist
[Company Name]

📧 [Email]
📞 [Phone]
🌐 [Website]

We're here to help! Feel free to reach out anytime.`
    }
  ];

  const insertTemplate = (template: string) => {
    setSignature(template);
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <EditNote />
          Message Signature Settings
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                color="primary"
              />
            }
            label="Enable signature for my messages"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            When enabled, your signature will be automatically added to outgoing messages
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Signature Content
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={6}
            placeholder="Enter your signature here..."
            value={signature}
            onChange={(e) => setSignature(e.target.value)}
            disabled={!enabled}
            helperText="You can use placeholders like [Agent Name], [Company Name], [Email], [Phone], [Website]"
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Quick Templates
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {templates.map((template, index) => (
              <Button
                key={index}
                variant="outlined"
                size="small"
                onClick={() => insertTemplate(template.content)}
                disabled={!enabled}
                sx={{ textTransform: 'none' }}
              >
                {template.name}
              </Button>
            ))}
          </Box>
        </Box>

        {signature && enabled && (
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Preview:
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                whiteSpace: 'pre-line',
                fontFamily: 'monospace',
                color: 'text.secondary'
              }}
            >
              {signature}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained">
          Save Signature
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SignatureDialog;